import React, { useState, useEffect } from 'react';
import '../styles/EcosystemAnalysis.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://market360-api.onrender.com';

// Oil ecosystem stocks organized by category
const OIL_ECOSYSTEM = {
  'Major Oil & Gas': {
    'XOM': { name: 'ExxonMobil', market: 'US' },
    'CVX': { name: 'Chevron', market: 'US' },
    'COP': { name: 'ConocoPhillips', market: 'US' },
    'OXY': { name: 'Occidental Petroleum', market: 'US' },
    'EOG': { name: 'EOG Resources', market: 'US' }
  },
  'Refining & Midstream': {
    'MPC': { name: 'Marathon Petroleum', market: 'US' },
    'PSX': { name: 'Phillips 66', market: 'US' },
    'VLO': { name: 'Valero Energy', market: 'US' },
    'SLB': { name: 'Schlumberger', market: 'US' },
    'HON': { name: 'Honeywell International', market: 'US' }
  },
  'Energy & Utilities': {
    'NEE': { name: 'NextEra Energy', market: 'US' },
    'DUK': { name: 'Duke Energy', market: 'US' },
    'SO': { name: 'Southern Company', market: 'US' },
    'WEC': { name: 'WEC Energy Group', market: 'US' },
    'ED': { name: 'Consolidated Edison', market: 'US' }
  },
  'Industrial & Equipment': {
    'CAT': { name: 'Caterpillar Inc', market: 'US' },
    'MMM': { name: '3M Company', market: 'US' },
    'ITW': { name: 'Illinois Tool Works', market: 'US' },
    'RTX': { name: 'Raytheon Technologies', market: 'US' },
    'BAC': { name: 'Bank of America', market: 'US' }
  },
  'Diversified Holdings': {
    'JPM': { name: 'JPMorgan Chase', market: 'US' },
    'MS': { name: 'Morgan Stanley', market: 'US' },
    'GS': { name: 'Goldman Sachs', market: 'US' },
    'WFC': { name: 'Wells Fargo', market: 'US' },
    'AEP': { name: 'American Electric Power', market: 'US' }
  }
};

export default function OilEcosystemAnalysis() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('change');
  const [refreshTime, setRefreshTime] = useState(new Date());

  useEffect(() => {
    fetchStockData();
    const interval = setInterval(() => {
      fetchStockData();
      setRefreshTime(new Date());
    }, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const fetchStockData = async () => {
    try {
      setLoading(true);
      const symbols = new Set();
      Object.values(OIL_ECOSYSTEM).forEach(category => {
        Object.keys(category).forEach(symbol => symbols.add(symbol));
      });

      // Fetch price and news data for all symbols
      const allStocks = [];
      for (const symbol of symbols) {
        try {
          const priceRes = await fetch(`${API_BASE_URL}/api/stock/${symbol}/details`);
          const priceData = priceRes.ok ? await priceRes.json() : null;
          
          const newsRes = await fetch(`${API_BASE_URL}/api/stock/${symbol}/news?limit=3`);
          const newsData = newsRes.ok ? await newsRes.json() : { news: [] };

          // Find category for this symbol
          let category = 'Other';
          for (const [cat, catStocks] of Object.entries(OIL_ECOSYSTEM)) {
            if (catStocks[symbol]) {
              category = cat;
              break;
            }
          }

          allStocks.push({
            symbol,
            category,
            name: OIL_ECOSYSTEM[category]?.[symbol]?.name || symbol,
            market: OIL_ECOSYSTEM[category]?.[symbol]?.market || 'US',
            current_price: priceData?.price || 0,
            day_change_percent: priceData?.change_percent || 0,
            day_change: priceData?.change || 0,
            market_cap: priceData?.market_cap || 0,
            pe_ratio: priceData?.pe_ratio || 0,
            dividend_yield: priceData?.dividend_yield || 0,
            news: newsData.news || []
          });
        } catch (err) {
          console.error(`Error fetching data for ${symbol}:`, err);
        }
      }

      setStocks(allStocks);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching ecosystem data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayedStocks = () => {
    let filtered = stocks;

    if (selectedCategory !== 'all') {
      filtered = stocks.filter(s => s.category === selectedCategory);
    }

    return filtered.sort((a, b) => {
      if (sortBy === 'change') {
        return (b.day_change_percent || 0) - (a.day_change_percent || 0);
      } else if (sortBy === 'price') {
        return (b.current_price || 0) - (a.current_price || 0);
      } else if (sortBy === 'symbol') {
        return a.symbol.localeCompare(b.symbol);
      }
      return 0;
    });
  };

  const displayedStocks = getDisplayedStocks();
  const categories = Object.keys(OIL_ECOSYSTEM);

  const getCategoryStats = () => {
    if (selectedCategory === 'all') {
      return {
        count: displayedStocks.length,
        avgChange: displayedStocks.length > 0 
          ? (displayedStocks.reduce((sum, s) => sum + (s.day_change_percent || 0), 0) / displayedStocks.length).toFixed(2)
          : 0,
        avgYield: displayedStocks.length > 0
          ? (displayedStocks.reduce((sum, s) => sum + (s.dividend_yield || 0), 0) / displayedStocks.length).toFixed(2)
          : 0
      };
    }
    return {
      count: displayedStocks.length,
      avgChange: displayedStocks.length > 0 
        ? (displayedStocks.reduce((sum, s) => sum + (s.day_change_percent || 0), 0) / displayedStocks.length).toFixed(2)
        : 0,
      avgYield: displayedStocks.length > 0
        ? (displayedStocks.reduce((sum, s) => sum + (s.dividend_yield || 0), 0) / displayedStocks.length).toFixed(2)
        : 0
    };
  };

  const stats = getCategoryStats();

  return (
    <div className="ecosystem-container">
      <div className="ecosystem-header">
        <h1>🛢️ Oil & Energy Ecosystem</h1>
        <p className="subtitle">Oil Production, Refining, Distribution & Energy Services</p>
        <div className="header-stats">
          <div className="stat">
            <span className="label">Stocks Monitored</span>
            <span className="value">{displayedStocks.length}</span>
          </div>
          <div className="stat">
            <span className="label">Avg Daily Change</span>
            <span className={`value ${stats.avgChange >= 0 ? 'positive' : 'negative'}`}>
              {stats.avgChange >= 0 ? '+' : ''}{stats.avgChange}%
            </span>
          </div>
          <div className="stat">
            <span className="label">Avg Dividend Yield</span>
            <span className="value">{stats.avgYield}%</span>
          </div>
          <div className="stat">
            <span className="label">Last Updated</span>
            <span className="value">{refreshTime.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      <div className="ecosystem-controls">
        <div className="control-group">
          <label>Category:</label>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div className="control-group">
          <label>Sort By:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="change">Daily Change %</option>
            <option value="price">Price</option>
            <option value="symbol">Symbol</option>
          </select>
        </div>
        <button className="refresh-btn" onClick={fetchStockData}>
          {loading ? 'Refreshing...' : 'Refresh Now'}
        </button>
      </div>

      {error && (
        <div className="error-box">
          <p>{error}</p>
          <button onClick={fetchStockData}>Retry</button>
        </div>
      )}

      {loading && displayedStocks.length === 0 ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading Oil Ecosystem data...</p>
        </div>
      ) : (
        <div className="stocks-grid">
          {displayedStocks.map(stock => (
            <div key={stock.symbol} className={`stock-card ${stock.day_change_percent >= 0 ? 'positive' : 'negative'}`}>
              <div className="stock-header">
                <div className="stock-info">
                  <h3>{stock.symbol}</h3>
                  <p className="stock-name">{stock.name}</p>
                  <span className="stock-category">{stock.category}</span>
                </div>
                <div className="stock-price">
                  <div className="price">${stock.current_price?.toFixed(2) || 'N/A'}</div>
                  <div className={`change ${stock.day_change_percent >= 0 ? 'positive' : 'negative'}`}>
                    {stock.day_change_percent >= 0 ? '▲' : '▼'} {stock.day_change_percent?.toFixed(2) || 0}%
                  </div>
                </div>
              </div>

              <div className="stock-details">
                <div className="detail">
                  <span className="label">Market Cap:</span>
                  <span className="value">
                    {stock.market_cap ? `$${(stock.market_cap / 1e9).toFixed(2)}B` : 'N/A'}
                  </span>
                </div>
                <div className="detail">
                  <span className="label">P/E Ratio:</span>
                  <span className="value">{stock.pe_ratio?.toFixed(2) || 'N/A'}</span>
                </div>
                <div className="detail">
                  <span className="label">Dividend Yield:</span>
                  <span className="value">{stock.dividend_yield?.toFixed(2) || 'N/A'}%</span>
                </div>
              </div>

              {stock.news && stock.news.length > 0 && (
                <div className="stock-news">
                  <h4>Latest News</h4>
                  {stock.news.map((article, idx) => (
                    <div key={idx} className="news-item">
                      <p className="news-title">{article.title?.substring(0, 60)}...</p>
                      <p className="news-source">{article.source}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="ecosystem-info">
        <h3>Oil & Energy Market Overview</h3>
        <div className="info-grid">
          <div className="info-item">
            <h4>Major Integrated Oil Companies</h4>
            <p>Vertically integrated with upstream, midstream & downstream operations. Dividend-focused. Leaders: XOM, CVX, COP.</p>
          </div>
          <div className="info-item">
            <h4>Oil Production & Exploration</h4>
            <p>Focused on upstream extraction and exploration. Benefits from oil price rallies. Key players: EOG, OXY, SU.</p>
          </div>
          <div className="info-item">
            <h4>Refining & Services</h4>
            <p>Midstream, refining, and oilfield services. Less volatile than producers. Examples: SLB, HAL, MPC, VLO.</p>
          </div>
          <div className="info-item">
            <h4>Pipeline & Infrastructure</h4>
            <p>Critical energy transport infrastructure. Stable cash flows and high dividends. Key: ENB, TRP, KMI, WMB.</p>
          </div>
        </div>
      </div>

      <div className="ecosystem-dynamics">
        <h3>Key Market Drivers</h3>
        <ul>
          <li><strong>WTI/Brent Crude Prices:</strong> Directly impacts profitability and valuations</li>
          <li><strong>Energy Transition:</strong> Pressure from renewables but continued global oil demand</li>
          <li><strong>Geopolitics:</strong> OPEC decisions, supply disruptions, sanctions</li>
          <li><strong>Dividend Yields:</strong> Many oil stocks offer 3-6% yields, attractive for income investors</li>
          <li><strong>Refining Margins:</strong> Spread between crude input and product output</li>
        </ul>
      </div>
    </div>
  );
}
